import { getCoverageLineConfig, getCoverageLinesConfig } from '@common/config';
import { SubmissionExtractedData } from '@common/types';
import { isEmpty } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Divider, Stack, Typography } from '@common-components';
import { CoverageLine, StateCode } from 'enums';
import { useFormProvider, useMutateCarrierSubmission, useScrollSections } from 'hooks';
import { messages } from 'i18n';
import { CarrierSubmission, CarrierSubmissionUpdate, Suggestion } from 'types';
import FormDatePicker from 'components/hookFormComponents/FormDatePicker';
import { FormMultiSelectAutocomplete } from 'components/hookFormComponents/FormMultiSelectAutocomplete';
import FormTextField from 'components/hookFormComponents/FormTextField';
import { SuggestionProps } from 'components/hookFormComponents/types';
import { FormWithStepper } from 'broker/components/common/FormWithStepper';
import IndustryAutoComplete from 'broker/components/common/IndustryAutoComplete';
import SubmissionFooter from 'broker/components/common/SubmissionFooter';
import { AIReason } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIReason';
import { useSendPromptReport } from 'broker/utils/use-send-prompt-report';
import CarrierSubmissionAddressFields from './CarrierSubmissionAddressFields';
import ComingSoonSection from './ComingSoonSection';
import {
  buildDefaultValues,
  carrierSubmissionFormSchema,
  CarrierSubmissionFormValues,
  useCarrierSubmissionExtractedData,
} from './useCarrierSubmissionForm';

const {
  sections: { basicInformation, clearanceStatus, complianceChecks, triageCompleteness },
  fields,
  save,
} = messages.carrierSubmissionWorkspace;

const sections = [
  { id: 'basic-information', label: basicInformation },
  { id: 'compliance-checks', label: complianceChecks },
  { id: 'clearance-status', label: clearanceStatus },
  { id: 'triage-completeness', label: triageCompleteness },
];

const coverageLineOptions = Object.entries(getCoverageLinesConfig()).map(([value]) => ({
  label: getCoverageLineConfig(value as CoverageLine).text,
  value,
}));

export interface CarrierSubmissionDetailsFormHandle {
  validateAndSave: () => Promise<boolean>;
}

interface CarrierSubmissionDetailsFormProps {
  carrierSubmission: CarrierSubmission;
  submissionExtractedData?: SubmissionExtractedData;
  onSave: () => void;
  onDirtyChange: (isDirty: boolean) => void;
}

const CarrierSubmissionDetailsForm = forwardRef<CarrierSubmissionDetailsFormHandle, CarrierSubmissionDetailsFormProps>(
  ({ carrierSubmission, submissionExtractedData, onSave, onDirtyChange }, ref) => {
    const { updateCarrierSubmission } = useMutateCarrierSubmission();
    const sendPromptReport = useSendPromptReport();
    const suggestedValues = useCarrierSubmissionExtractedData(submissionExtractedData);

    const defaultValues = useMemo(() => buildDefaultValues(carrierSubmission), [carrierSubmission]);

    // Derive first visit from persisted data, matching EditSubmissionContent's firstVisit pattern.
    // Once coverageLines are saved, subsequent visits won't re-apply suggestions.
    const isFirstVisit = useMemo(() => isEmpty(carrierSubmission.submission.coverageLines), [carrierSubmission]);

    const [isDirty, setIsDirty] = useState(false);

    const { methods, appliedSuggestions } = useFormProvider<CarrierSubmissionFormValues>({
      schema: carrierSubmissionFormSchema,
      defaultValues,
      suggestedValues,
      applySuggestionsOnEmptyFields: isFirstVisit,
      setIsDirty,
    });

    useEffect(() => {
      onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    // Stepper state — mirrors useDynamicFormPersistentState pattern
    const [activeSection, setActiveSection] = useState<string | undefined>(sections[0].id);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const scrollableDivRef = useRef<HTMLDivElement | null>(null);
    const isScrollingProgrammatically = useRef(false);

    // Wrap setActiveSection to ignore scroll-spy updates during programmatic scrolling
    const scrollSetActiveSection = useCallback((section: string | undefined) => {
      if (!isScrollingProgrammatically.current) {
        setActiveSection(section);
      }
    }, []);

    useScrollSections({
      scrollableDivRef,
      sectionRefs,
      activeSection,
      setActiveSection: scrollSetActiveSection,
    });

    const onSetActiveSection = useCallback((section?: string) => {
      isScrollingProgrammatically.current = true;
      setActiveSection(section);
      if (section && sectionRefs.current[section]) {
        sectionRefs.current[section]?.scrollIntoView({ behavior: 'smooth' });
      }
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    }, []);

    // Build suggestion overlays per field
    const coverageLinesSuggestion = useMemo(
      () =>
        Array.isArray(appliedSuggestions.coverageLines?.value)
          ? {
              value: (appliedSuggestions.coverageLines.value as CoverageLine[]).map(
                (cl) => getCoverageLineConfig(cl).text,
              ),
              reason: <AIReason suggestion={appliedSuggestions.coverageLines as Suggestion} />,
              confidence: appliedSuggestions.coverageLines?.confidence,
            }
          : undefined,
      [appliedSuggestions],
    );

    const resolvedSuggestions = useMemo(() => {
      const resolved: Record<string, SuggestionProps> = {};
      Object.entries(appliedSuggestions).forEach(([key, suggestion]) => {
        if (key === 'coverageLines' || key === 'insuredMailingAddress') {
          return; // handled separately above
        }
        resolved[key] = {
          value: suggestion.value,
          reason: <AIReason suggestion={suggestion as Suggestion} />,
          confidence: suggestion.confidence,
        };
      });
      return resolved;
    }, [appliedSuggestions]);

    const submitForm = async () => {
      const formValues = methods.getValues();
      const payload: CarrierSubmissionUpdate = {
        agentName: formValues.agentName,
        insuredName: formValues.insuredName,
        industry: formValues.industry
          ? {
              heraldId: formValues.industry.heraldId,
              naicsCode: formValues.industry.naicsCode,
              description: formValues.industry.description,
            }
          : undefined,
        insuredMailingAddress: !isEmpty(formValues.insuredMailingAddress?.line1)
          ? {
              formattedAddress: [
                formValues.insuredMailingAddress.line1,
                formValues.insuredMailingAddress.city,
                formValues.insuredMailingAddress.state,
                formValues.insuredMailingAddress.postalCode,
              ]
                .filter(Boolean)
                .join(', '),
              line1: formValues.insuredMailingAddress.line1,
              line2: formValues.insuredMailingAddress.line2,
              city: formValues.insuredMailingAddress.city,
              state: formValues.insuredMailingAddress.state as StateCode,
              postalCode: formValues.insuredMailingAddress.postalCode,
              country: formValues.insuredMailingAddress.country,
            }
          : undefined,
        coverageLines: formValues.coverageLines as CoverageLine[],
        dueDate: formValues.dueDate ? new Date(formValues.dueDate) : undefined,
        sic: formValues.sic,
        natureOfBusiness: formValues.natureOfBusiness,
        fein: formValues.fein,
        ...(!isEmpty(formValues.businessEmail) ? { businessEmail: formValues.businessEmail } : {}),
      };
      await updateCarrierSubmission.mutateAsync({
        id: carrierSubmission.id,
        data: payload,
      });
      if (suggestedValues) {
        sendPromptReport({
          formState: methods.getValues(),
          suggestedValueProps: suggestedValues,
          submissionId: carrierSubmission.submission.id,
          entityId: carrierSubmission.id,
        });
      }
    };

    const handleSave = () => {
      methods.handleSubmit(async () => {
        await submitForm();
        onSave();
      })();
    };

    const validateAndSave = async (): Promise<boolean> => {
      const isValid = await methods.trigger();
      if (!isValid) {
        return false;
      }
      try {
        await submitForm();
        return true;
      } catch {
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      validateAndSave,
    }));

    // Access formState.errors during render to subscribe to error changes in RHF's proxy.
    // Without this, memoized FormTextField children won't re-render to display validation errors.
    // This mirrors how DynamicFormLayout subscribes via useGetSectionsIndicators.
    const hasErrors = Object.keys(methods.formState.errors).length > 0;

    const sectionList = useMemo(
      () =>
        sections.map((section) => {
          if (section.id === 'basic-information') {
            return {
              ...section,
              hasError: hasErrors,
            };
          }
          // Sections with no fields are considered completed
          return {
            ...section,
            isCompleted: true,
          };
        }),
      [hasErrors],
    );

    return (
      <FormProvider {...methods}>
        <Stack flex={1} minHeight={0} pt={4}>
          <FormWithStepper
            sectionProps={{
              activeSection,
              setActiveSection: onSetActiveSection,
              sectionList,
            }}
            ref={scrollableDivRef}
            withPadding
          >
            <Stack divider={<Divider sx={{ mt: 2, mb: 5 }} />}>
              <Stack
                ref={(el) => {
                  sectionRefs.current['basic-information'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {basicInformation}
                </Typography>
                <FormTextField
                  name="insuredName"
                  id="insuredName"
                  label={fields.insuredName}
                  suggestion={resolvedSuggestions.insuredName}
                />
                <FormTextField
                  name="agentName"
                  id="agentName"
                  label={fields.agentName}
                  suggestion={resolvedSuggestions.agentName}
                />
                <FormMultiSelectAutocomplete
                  name="coverageLines"
                  label={fields.lineOfBusiness}
                  options={coverageLineOptions}
                  suggestion={coverageLinesSuggestion}
                />
                <FormDatePicker
                  name="dueDate"
                  id="dueDate"
                  label={fields.effectiveDate}
                  suggestion={resolvedSuggestions.dueDate}
                />
                <CarrierSubmissionAddressFields
                  suggestion={appliedSuggestions.insuredMailingAddress as Suggestion | undefined}
                />
                <FormTextField
                  name="businessEmail"
                  id="businessEmail"
                  label={fields.businessEmail}
                  suggestion={resolvedSuggestions.businessEmail}
                />
                <IndustryAutoComplete
                  fieldProps={{ name: 'industry', label: fields.industry }}
                  suggestion={resolvedSuggestions.industry}
                />
                <FormTextField
                  name="natureOfBusiness"
                  id="natureOfBusiness"
                  label={fields.natureOfBusiness}
                  suggestion={resolvedSuggestions.natureOfBusiness}
                />
                <FormTextField name="sic" id="sic" label={fields.sicCode} suggestion={resolvedSuggestions.sic} />
                <FormTextField name="fein" id="fein" label={fields.taxIdFein} suggestion={resolvedSuggestions.fein} />
              </Stack>

              <Stack
                ref={(el) => {
                  sectionRefs.current['compliance-checks'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {complianceChecks}
                </Typography>
                <ComingSoonSection />
              </Stack>

              <Stack
                ref={(el) => {
                  sectionRefs.current['clearance-status'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {clearanceStatus}
                </Typography>
                <ComingSoonSection />
              </Stack>

              <Stack
                ref={(el) => {
                  sectionRefs.current['triage-completeness'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {triageCompleteness}
                </Typography>
                <ComingSoonSection />
              </Stack>
            </Stack>
          </FormWithStepper>
          <Stack px={2} py={1} borderTop={1} borderColor="divider">
            <SubmissionFooter
              onSubmit={handleSave}
              isSubmitting={methods.formState.isSubmitting}
              submitButtonText={save}
              active={activeSection}
              setActiveSection={onSetActiveSection}
              list={sections.map((s) => s.id)}
            />
          </Stack>
        </Stack>
      </FormProvider>
    );
  },
);

CarrierSubmissionDetailsForm.displayName = 'CarrierSubmissionDetailsForm';

export default CarrierSubmissionDetailsForm;
